import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight, ChevronDown, ShieldCheck, Star, CheckCircle2,
  // ── feature icons (whitelisted to keep bundle slim) ──
  FileText, Shield, Zap, BarChart3, IndianRupee, Users, Lock,
  Layers, RefreshCw, Server, Eye, Building2, Briefcase, Network, Send, UserCheck, Receipt, TrendingUp,
} from "lucide-react";
import { SEO_LANDING_PAGES } from "../seo/landingPagesConfig";
import SEO from "../seo/SEO";
import { ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA, buildBreadcrumbSchema, buildFAQSchema } from "../seo/seoConfig";

const ICON_MAP = {
  FileText, Shield, Zap, BarChart3, IndianRupee, Users, Lock, Layers,
  RefreshCw, Server, Eye, Building2, Briefcase, Network, Send, UserCheck, Receipt, TrendingUp,
};

const heading = { fontFamily: "'Satoshi', sans-serif" };

export default function SeoLandingTemplate({ slug: slugProp }) {
  const params = useParams();
  const navigate = useNavigate();
  const slug = slugProp || params.slug;
  const page = SEO_LANDING_PAGES.find((p) => p.slug === slug);

  if (!page) return <Navigate to="/" replace />;

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: page.h1.split("—")[0].trim(), url: `/${page.slug}` },
  ];

  const schemas = [
    ORGANIZATION_SCHEMA,
    SOFTWARE_SCHEMA,
    buildBreadcrumbSchema(breadcrumbs),
    buildFAQSchema(page.faqs),
  ];

  return (
    <div className="bg-white" data-testid={`seo-landing-${page.slug}`}>
      <SEO
        title={page.h1.split("—")[0].trim()}
        description={page.description}
        path={`/${page.slug}`}
        schemas={schemas}
      />

      {/* ── Mini header with brand link ── */}
      <div className="bg-white/85 backdrop-blur sticky top-0 z-30 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-home-link">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-7 h-7 rounded-md" />
            <span className="text-base font-bold text-slate-900" style={heading}>
              Invoice<span className="text-blue-600">Setu</span>
            </span>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/blog" className="text-slate-600 hover:text-slate-900 hidden sm:inline px-3 py-1.5">Blog</Link>
            <Link to="/for-ca" className="text-slate-600 hover:text-slate-900 hidden sm:inline px-3 py-1.5">For CAs</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 hidden sm:inline px-3 py-1.5">Pricing</Link>
            <button
              onClick={() => navigate(page.primaryCta.href)}
              className="bg-blue-600 text-white font-semibold rounded-lg px-4 py-1.5 hover:bg-blue-700 transition-colors text-xs sm:text-sm"
              data-testid="seo-header-cta"
            >
              Start Free
            </button>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="px-4 sm:px-6 py-12 sm:py-20" data-testid="seo-hero">
        <div className="max-w-4xl mx-auto text-center">
          {page.badge && (
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 mb-5">
              {page.badge}
            </span>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="text-3xl sm:text-5xl font-black text-slate-900 leading-[1.1] tracking-tight mb-5"
            style={heading}
          >
            {page.h1}
          </motion.h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
            {page.subhead}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <Link
              to={page.primaryCta.href}
              data-testid="seo-hero-primary"
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-6 py-3 hover:bg-blue-700 transition-colors shadow-[0_4px_14px_rgba(37,99,235,0.25)]"
              style={heading}
            >
              {page.primaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
            {page.secondaryCta && (
              <Link
                to={page.secondaryCta.href}
                data-testid="seo-hero-secondary"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-900 font-semibold rounded-lg px-6 py-3 hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                {page.secondaryCta.label}
              </Link>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Free plan available</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No credit card</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Setup in 2 minutes</span>
          </div>
        </div>
      </section>

      {/* ── Features grid ── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-slate-50/50 border-y border-slate-100" data-testid="seo-features">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-10" style={heading}>
            Everything you need, nothing you don’t.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.features.map((f, i) => {
              const Icon = ICON_MAP[f.icon] || Shield;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="bg-white rounded-xl border border-slate-100 p-5 hover:border-blue-200 hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1.5" style={heading}>{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Steps ── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16" data-testid="seo-steps">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-10" style={heading}>
            How it works
          </h2>
          <ol className="space-y-4">
            {page.steps.map((s, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4 bg-white rounded-xl border border-slate-100 p-4"
              >
                <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <p className="text-slate-700 pt-1">{s}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Trust strip ── */}
      <section className="px-4 sm:px-6 py-10 bg-gradient-to-b from-white to-slate-50/60" data-testid="seo-trust">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { n: "10,000+", l: "Invoices created" },
            { n: "1,500+", l: "Indian businesses" },
            { n: "₹50 Cr+", l: "Billed through us" },
            { n: "4.9 / 5", l: "Average rating" },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-2xl sm:text-3xl font-black text-slate-900" style={heading}>{s.n}</div>
              <div className="text-xs text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 border-y border-slate-100" data-testid="seo-faqs">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-10" style={heading}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-2">
            {page.faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="px-4 sm:px-6 py-14 sm:py-20 bg-blue-600 text-white" data-testid="seo-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl sm:text-3xl font-black mb-3" style={heading}>
            Start in 2 minutes. Free forever.
          </h2>
          <p className="text-blue-100 mb-7">
            Join 1,500+ Indian businesses that switched to InvoiceSetu — no credit card, no setup fee.
          </p>
          <Link
            to={page.primaryCta.href}
            data-testid="seo-bottom-cta"
            className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold rounded-lg px-7 py-3.5 hover:bg-blue-50 transition-colors"
            style={heading}
          >
            {page.primaryCta.label} <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-blue-100 mt-6">
            <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" /> 4.9 / 5</span>
            <span>·</span>
            <span>10,000+ invoices generated</span>
            <span>·</span>
            <span>India-first by design</span>
          </div>
        </div>
      </section>

      {/* ── Cross-link other landing pages for internal SEO ── */}
      <section className="px-4 sm:px-6 py-10 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.18em] mb-4 text-center">Explore more</p>
          <div className="flex flex-wrap justify-center gap-2">
            {SEO_LANDING_PAGES.filter((p) => p.slug !== slug).slice(0, 8).map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="text-xs sm:text-sm bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:text-blue-700 hover:border-blue-300 transition-colors"
              >
                {p.h1.split("—")[0].trim()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-7 h-7 rounded-md" />
            <span className="text-sm font-bold text-slate-900" style={heading}>
              Invoice<span className="text-blue-600">Setu</span>
            </span>
          </Link>
          <p className="text-xs text-slate-400 text-center">India’s smart invoice software for small businesses</p>
          <div className="flex gap-4 text-xs">
            <Link to="/auth" className="text-slate-500 hover:text-slate-800">Sign in</Link>
            <Link to="/pricing" className="text-slate-500 hover:text-slate-800">Pricing</Link>
            <Link to="/blog" className="text-slate-500 hover:text-slate-800">Blog</Link>
            <Link to="/for-ca" className="text-slate-500 hover:text-slate-800">For CAs</Link>
            <Link to="/sample/invoice" className="text-slate-500 hover:text-slate-800">Sample invoice</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100" data-testid={`seo-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
